import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faKey, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { Modal } from 'react-bootstrap';
import Button, { ButtonVariant } from 'components/base/Button';
import { useImportarPorChaveNFe } from 'hooks/useNFe';
import { useEmpresas } from 'providers/EmpresaProvider';
import { useToast } from 'providers/ToastProvider';

interface BotaoImportarPorChaveNFeProps {
  size?: 'sm' | 'lg';
  variant?: ButtonVariant;
  className?: string;
}

const BotaoImportarPorChaveNFe: React.FC<BotaoImportarPorChaveNFeProps> = ({
  size,
  variant = 'outline-secondary',
  className = '',
}) => {
  const navigate = useNavigate();
  const { selectedIds } = useEmpresas();
  const { showToast } = useToast();
  const empresaId = selectedIds[0];

  const [show, setShow] = useState(false);
  const [chave, setChave] = useState('');
  const [chaveError, setChaveError] = useState('');

  const { mutate: importar, isPending } = useImportarPorChaveNFe();

  const handleOpen = () => {
    if (!empresaId) {
      showToast('Selecione uma empresa antes de importar por chave.', 'error');
      return;
    }
    setChave('');
    setChaveError('');
    setShow(true);
  };

  const handleChaveChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const valor = e.target.value.replace(/\D/g, '').slice(0, 44);
    setChave(valor);
    if (chaveError) setChaveError('');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (chave.length !== 44) {
      setChaveError('A chave de acesso deve ter exatamente 44 dígitos');
      return;
    }

    importar(
      { chaveAcesso: chave, empresaId },
      {
        onSuccess: (data) => {
          setShow(false);
          navigate(`/operacional/nfe/${data.emissaoId}`);
        },
        onError: (error: any) => {
          const msg =
            error?.response?.data?.message ??
            'Erro ao importar NF-e. Verifique a chave e tente novamente.';
          setChaveError(msg);
        },
      }
    );
  };

  return (
    <>
      <Button
        variant={variant}
        size={size}
        onClick={handleOpen}
        disabled={!empresaId}
        className={`d-flex align-items-center ${className}`}
      >
        <FontAwesomeIcon icon={faKey} className="me-2" />
        Importar por Chave
      </Button>

      <Modal show={show} onHide={() => !isPending && setShow(false)} centered>
        <form onSubmit={handleSubmit}>
          <Modal.Header closeButton={!isPending}>
            <Modal.Title className="fs-6 fw-semibold">Importar NF-e por Chave de Acesso</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="text-body-secondary small mb-3">
              Digite os 44 dígitos da chave de acesso da NF-e. O XML será baixado automaticamente do SEFAZ.
            </p>
            <div>
              <label className="form-label small fw-medium">Chave de Acesso</label>
              <input
                type="text"
                className={`form-control font-monospace${chaveError ? ' is-invalid' : ''}`}
                placeholder="00000000000000000000000000000000000000000000"
                value={chave}
                onChange={handleChaveChange}
                maxLength={44}
                autoFocus
                disabled={isPending}
              />
              {chaveError && <div className="invalid-feedback">{chaveError}</div>}
              <div className="form-text">{chave.length}/44 dígitos</div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="phoenix-secondary" onClick={() => setShow(false)} disabled={isPending}>
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={isPending || chave.length !== 44}
            >
              {isPending ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                  Buscando...
                </>
              ) : (
                'Importar'
              )}
            </Button>
          </Modal.Footer>
        </form>
      </Modal>
    </>
  );
};

export default BotaoImportarPorChaveNFe;
